import React from 'react';
import { BsTwitter, BsFacebook, BsLinkedin } from 'react-icons/bs';
import { FaClock, FaEye, FaHeart, FaShare } from 'react-icons/fa';
import { format } from 'date-fns';
import CommentSection from '../common/CommentSection';
import RelatedPosts from '../common/RelatedPosts';
import AuthorInfo from '../common/AuthorInfo';
import CategoryList from '../common/CategoryList';
import SearchBar from '../common/SearchBar';
import RecentPosts from '../common/RecentPosts';
import PopularPosts from '../common/PopularPosts';
import TagsList from '../common/TagsList';
import StatusBadge from '../../common/StatusBadge';

const CaseStudyDetail = ({ caseStudy }) => {
  if (!caseStudy) return <div>Loading...</div>;

  const handleShare = async (platform) => {
    const url = window.location.href;
    const title = caseStudy.title;
    let shareUrl = '';

    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/share?url=${url}&text=${title}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/shareArticle?url=${url}&title=${title}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="container mx-auto py-8">
      {/* Full Width Feature Image with Overlay Title */}
      <div className="relative w-full h-96 mb-8">
        {caseStudy.featureImage && (
          <img
            src={caseStudy.featureImage}
            alt={caseStudy.title}
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col justify-center items-center text-white p-4">
          <h1 className="text-5xl font-bold text-center">{caseStudy.title}</h1>
          <div className="flex items-center space-x-4 mt-4">
            <span className="flex items-center">
              <FaClock className="mr-2" />
              {caseStudy.projectDetails?.duration || 'N/A'}
            </span>
            <span className="flex items-center">
              <FaEye className="mr-2" />
              {caseStudy.metadata?.views || 0} views
            </span>
            <span className="flex items-center">
              <FaHeart className="mr-2" />
              {caseStudy.metadata?.likes || 0} likes
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Main Content Column */}
        <div className="col-span-3 space-y-8">
          {/* Row 1 - Social Icons */}
          <div className="flex justify-center space-x-4 mb-4">
            <button onClick={() => handleShare('twitter')} className="text-blue-500 hover:text-blue-600">
              <BsTwitter size={24} />
            </button>
            <button onClick={() => handleShare('facebook')} className="text-blue-800 hover:text-blue-900">
              <BsFacebook size={24} />
            </button>
            <button onClick={() => handleShare('linkedin')} className="text-blue-600 hover:text-blue-700">
              <BsLinkedin size={24} />
            </button>
            <button onClick={() => handleShare('share')} className="text-gray-600 hover:text-gray-700">
              <FaShare size={24} />
            </button>
          </div>

          {/* Row 2 - Title and Meta Info */}
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div className="flex items-center space-x-4">
              <span>By {caseStudy.authorName}</span>
              <span>•</span>
              <span>{format(new Date(caseStudy.publishedAt || caseStudy.createdAt), 'MMM d, yyyy')}</span>
              <StatusBadge status={caseStudy.status} />
            </div>
          </div>

          {/* Row 3 - Short Description */}
          {caseStudy.shortDescription && (
            <div className="text-lg text-gray-600 dark:text-gray-400 italic">
              {caseStudy.shortDescription}
            </div>
          )}

          {/* Row 4 - Client Information */}
          {caseStudy.client && (
            <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
              <h2 className="text-2xl font-bold mb-4">Client Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="font-semibold">Client Name</p>
                  <p>{caseStudy.client.name}</p>
                </div>
                <div>
                  <p className="font-semibold">Industry</p>
                  <p>{caseStudy.client.industry}</p>
                </div>
                {caseStudy.client.website && (
                  <div className="md:col-span-2">
                    <p className="font-semibold">Website</p>
                    <a href={caseStudy.client.website} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600">
                      {caseStudy.client.website}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Row 5 - Project Details */}
          {caseStudy.projectDetails && (
            <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
              <h2 className="text-2xl font-bold mb-4">Project Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="font-semibold mb-2">Duration</p>
                  <p>{caseStudy.projectDetails.duration}</p>
                </div>
                <div>
                  <p className="font-semibold mb-2">Team Size</p>
                  <p>{caseStudy.projectDetails.teamSize} members</p>
                </div>
                <div className="md:col-span-2">
                  <p className="font-semibold mb-2">Technologies Used</p>
                  <div className="flex flex-wrap gap-2">
                    {caseStudy.projectDetails.technologies.map((tech, index) => (
                      <span key={index} className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-sm">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Row 6 - Content */}
          <div className="rich-text-content text-gray-700 dark:text-gray-300 leading-relaxed" 
               dangerouslySetInnerHTML={{ __html: caseStudy.content }} />

          {/* Row 7 - Metrics */}
          {caseStudy.metrics && (
            <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
              <h2 className="text-2xl font-bold mb-4">Project Metrics</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {caseStudy.metrics.revenue && (
                  <div>
                    <p className="font-semibold mb-2">Revenue Impact</p>
                    <p className="text-2xl font-bold text-green-600">{caseStudy.metrics.revenue}</p>
                  </div>
                )}
                {caseStudy.metrics.efficiency && (
                  <div>
                    <p className="font-semibold mb-2">Efficiency Gain</p>
                    <p className="text-2xl font-bold text-blue-600">{caseStudy.metrics.efficiency}</p>
                  </div>
                )}
                {caseStudy.metrics.userGrowth && (
                  <div>
                    <p className="font-semibold mb-2">User Growth</p>
                    <p className="text-2xl font-bold text-purple-600">{caseStudy.metrics.userGrowth}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Row 8 - Testimonials */}
          {caseStudy.testimonials && caseStudy.testimonials.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
              <h2 className="text-2xl font-bold mb-4">Client Testimonials</h2>
              <div className="space-y-6">
                {caseStudy.testimonials.map((testimonial, index) => (
                  <div key={index} className="border-l-4 border-blue-500 pl-4">
                    <p className="text-lg italic mb-2">"{testimonial.quote}"</p>
                    <p className="font-semibold">{testimonial.author}</p>
                    <p className="text-gray-600 dark:text-gray-400">{testimonial.position}, {testimonial.company}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Row 9 - Categories and Tags */}
          {(caseStudy.categoryNames?.length > 0 || caseStudy.tagNames?.length > 0) && (
            <div className="flex flex-wrap gap-2">
              {caseStudy.categoryNames?.map((category, index) => (
                <span
                  key={index}
                  className="px-3 py-1 text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full"
                >
                  {category}
                </span>
              ))}
              {caseStudy.tagNames?.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 text-sm font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Row 10 - Author Info */}
          <AuthorInfo author={caseStudy.author} />

          {/* Row 11 - Comments Section */}
          {caseStudy.commentsEnabled && (
            <CommentSection caseStudyId={caseStudy._id} />
          )}
        </div>

        {/* Sidebar Column */}
        <div className="col-span-1 space-y-6">
          <SearchBar />
          <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
            <h2 className="text-lg font-bold text-gray-700 dark:text-gray-200">About Our Case Studies</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Explore our portfolio of successful projects and learn how we've helped businesses achieve their goals.
            </p>
          </div>
          <CategoryList />
          <RecentPosts />
          <PopularPosts />
          <TagsList />
        </div>
      </div>
    </div>
  );
};

export default CaseStudyDetail; 